import { useParams, Link, Navigate } from "react-router-dom";
import PageBanner from "../components/PageBanner";
import { findSubProduct, products, company } from "../mock/mock";
import { CheckCircle2, Phone, Mail, MessageCircle, ArrowRight } from "lucide-react";

export default function SubProductDetail() {
  const { slug, subSlug } = useParams();
  const result = findSubProduct(slug, subSlug);
  if (!result) return <Navigate to="/products" replace />;
  const { product, sub } = result;
  const others = product.subProducts.filter((x) => x.slug !== sub.slug);

  return (
    <div>
      <PageBanner
        title={sub.name}
        subtitle={sub.short}
        breadcrumbs={[
          { label: "Products", to: "/products" },
          { label: product.name, to: `/products/${product.slug}` },
          { label: sub.name },
        ]}
      />

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-3 gap-10">
          <aside className="lg:col-span-1">
            <div className="sticky top-28">
              <div className="bg-[#0d2240] text-white rounded-xl p-5">
                <h4 className="font-heading font-bold text-base uppercase tracking-widest border-b border-white/15 pb-3">
                  {product.name} Variants
                </h4>
                <ul className="mt-3">
                  {product.subProducts.map((s) => (
                    <li key={s.id}>
                      <Link
                        to={`/products/${product.slug}/${s.slug}`}
                        className={`block py-2 px-3 rounded-md text-[13px] ${
                          s.slug === sub.slug ? "bg-[#c8102e] text-white" : "text-white/80 hover:bg-white/10"
                        }`}
                      >
                        {s.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-5 bg-white border border-gray-100 rounded-xl p-5">
                <h4 className="font-heading font-bold text-[#0d2240]">Get a Quick Quote</h4>
                <p className="text-sm text-gray-600 mt-1">Click any contact below to reach our sales team.</p>
                <a href={`tel:+91${company.phone}`} className="mt-3 inline-flex items-center gap-2 text-[#c8102e] font-semibold text-sm"><Phone size={14} /> +91 {company.phone}</a>
                <a href={`https://wa.me/91${company.whatsapp}`} target="_blank" rel="noreferrer" className="mt-2 flex items-center gap-2 text-[#25D366] font-semibold text-sm"><MessageCircle size={14} /> +91 {company.whatsapp}</a>
                <a href={`mailto:${company.email}`} className="mt-2 flex items-center gap-2 text-[#0d2240] font-semibold text-sm break-all"><Mail size={14} /> {company.email}</a>
              </div>
            </div>
          </aside>

          <div className="lg:col-span-2">
            <div className="grid md:grid-cols-2 gap-6 items-start">
              <div className="rounded-xl overflow-hidden border border-gray-100">
                <img src={sub.image} alt={sub.name} className="w-full h-80 object-cover" />
              </div>
              <div>
                <span className="inline-block text-[11px] uppercase tracking-widest text-[#c8102e] font-semibold mb-2">{product.name}</span>
                <h2 className="font-heading font-extrabold text-2xl md:text-3xl text-[#0d2240]">{sub.name}</h2>
                <p className="text-gray-600 leading-relaxed mt-4">{sub.description}</p>

                <div className="flex flex-wrap gap-2 mt-5">
                  <a href={`mailto:${company.email}?subject=Inquiry%20about%20${encodeURIComponent(sub.name)}`} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-[#c8102e] hover:bg-[#9a0c24] text-white text-sm font-semibold">
                    Email Inquiry <ArrowRight size={14} />
                  </a>
                  <a href={`https://wa.me/91${company.whatsapp}?text=Hello%20Mahalaxmi%2C%20I%20am%20interested%20in%20${encodeURIComponent(sub.name)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-[#25D366] hover:bg-[#1ebe5a] text-white text-sm font-semibold">
                    <MessageCircle size={14} /> WhatsApp
                  </a>
                </div>
              </div>
            </div>

            <div className="mt-10 grid md:grid-cols-2 gap-5">
              <div className="bg-white rounded-xl border border-gray-100 p-5">
                <h4 className="font-heading font-bold text-[#0d2240] mb-3">Key Features</h4>
                <ul className="flex flex-col gap-2">
                  {sub.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-[#0d2240]">
                      <CheckCircle2 size={16} className="text-[#c8102e] mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white rounded-xl border border-gray-100 p-5">
                <h4 className="font-heading font-bold text-[#0d2240] mb-3">Applications</h4>
                <ul className="flex flex-wrap gap-2">
                  {sub.applications.map((a) => (
                    <li key={a} className="text-[12px] px-3 py-1.5 rounded-full bg-[#f7f7f9] border border-gray-100 text-[#0d2240]">{a}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-5 bg-white rounded-xl border border-gray-100 p-5">
              <h4 className="font-heading font-bold text-[#0d2240] mb-3">Specifications</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <tbody>
                    {Object.entries(sub.specs).map(([k, v]) => (
                      <tr key={k} className="border-b border-gray-100 last:border-0">
                        <td className="py-2.5 px-3 font-semibold text-[#0d2240] capitalize w-1/3 bg-[#f7f7f9]">{k}</td>
                        <td className="py-2.5 px-3 text-gray-700">{v}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {others.length > 0 && (
              <div className="mt-12">
                <h3 className="font-heading font-bold text-xl text-[#0d2240] mb-5">More {product.name}</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  {others.map((s) => (
                    <Link key={s.id} to={`/products/${product.slug}/${s.slug}`} className="card-lift flex gap-3 bg-white p-3 rounded-xl border border-gray-100">
                      <div className="w-24 h-24 shrink-0 rounded-lg overflow-hidden bg-[#f7f7f9]">
                        <img src={s.image} alt={s.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-heading font-semibold text-[#0d2240] text-[14px]">{s.name}</h4>
                        <p className="text-[12px] text-gray-600 mt-1 line-clamp-2">{s.short}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
